import { memo, useEffect, useRef } from "react";
import { LocalVideoTrack } from "livekit-client";
import CameraArea from "./camera-area";

interface WebcamProps {
  localVideoTrack: LocalVideoTrack | null | undefined;
}

function WebcamComponent({ localVideoTrack }: WebcamProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevTrackRef = useRef<LocalVideoTrack | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    const track = localVideoTrack;

    if (!container || !track) {
      if (prevTrackRef.current) {
        prevTrackRef.current.detach().forEach((el) => el.remove());
        prevTrackRef.current = null;
      }
      if (container) container.replaceChildren();
      return;
    }

    if (prevTrackRef.current === track) return;

    if (prevTrackRef.current) {
      prevTrackRef.current.detach().forEach((el) => el.remove());
    }

    const videoEl = track.attach() as HTMLVideoElement;
    videoEl.muted = true;
    videoEl.playsInline = true;
    Object.assign(videoEl.style, {
      width: "100%",
      height: "100%",
      objectFit: "cover",
      display: "block",
      transform: "scaleX(-1)",
    });

    container.replaceChildren(videoEl);
    prevTrackRef.current = track;

    return () => {
      track.detach().forEach((el) => el.remove());
      if (prevTrackRef.current === track) {
        prevTrackRef.current = null;
      }
    };
  }, [localVideoTrack]);

  return <CameraArea ref={containerRef} />;
}

const areEqual = (prev: WebcamProps, next: WebcamProps) => {
  const p = prev.localVideoTrack?.mediaStreamTrack?.id;
  const n = next.localVideoTrack?.mediaStreamTrack?.id;
  return p === n;
};

export default memo(WebcamComponent, areEqual);
